import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { USERNAME_PATTERN, operatorRoleSchema, type OperatorRole } from '@/schemas/users';
import { CopyButton } from './copy-button';

const schema = z
  .object({
    mode: z.enum(['invite', 'temp']),
    username: z.string().regex(USERNAME_PATTERN, 'errors.validation.usernameInvalid'),
    email: z.string().optional(),
    password: z.string().optional(),
    role: operatorRoleSchema,
  })
  .superRefine((values, ctx) => {
    if (values.mode === 'invite') {
      if (!values.email || !z.string().email().safeParse(values.email).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['email'],
          message: 'errors.validation.emailInvalid',
        });
      }
    } else if (!values.password || values.password.length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'errors.validation.passwordTooShort',
      });
    }
  });

export type CreateUserFormValues = z.infer<typeof schema>;

const ROLES: OperatorRole[] = ['admin', 'developer_operator', 'manufacturer_operator'];

interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CreateUserFormValues) => void;
  isLoading?: boolean;
  serverError?: string | null;
  /** Set when SMTP is degraded and the backend returned a one-time manual link. */
  inviteUrl?: string | null;
}

export default function CreateUserDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  serverError,
  inviteUrl = null,
}: CreateUserDialogProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = React.useState(false);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateUserFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { mode: 'invite', role: 'developer_operator' },
  });

  React.useEffect(() => {
    if (open) {
      reset({ mode: 'invite', role: 'developer_operator' });
      setShowPassword(false);
    }
  }, [open, reset]);

  const role = watch('role');
  const mode = watch('mode');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {inviteUrl ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('operators.inviteLink.title')}</DialogTitle>
              <DialogDescription>{t('operators.inviteLink.degradeDescription')}</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2">
              <code className="min-w-0 flex-1 break-all font-mono text-[12.5px] text-foreground">
                {inviteUrl}
              </code>
              <CopyButton
                text={inviteUrl}
                label={t('operators.inviteLink.copy')}
                data-testid="copy-invite-url"
                className="shrink-0"
              />
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>{t('operators.inviteLink.done')}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('operators.createDialog.title')}</DialogTitle>
              <DialogDescription>{t('operators.createDialog.description')}</DialogDescription>
            </DialogHeader>
            <form
              noValidate
              onSubmit={handleSubmit((data) => onSubmit(data))}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label>{t('operators.createDialog.modeLabel')}</Label>
                <Tabs
                  value={mode}
                  onValueChange={(v) =>
                    setValue('mode', v as CreateUserFormValues['mode'], { shouldValidate: false })
                  }
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="invite">
                      {t('operators.createDialog.modeInvite')}
                    </TabsTrigger>
                    <TabsTrigger value="temp">{t('operators.createDialog.modeTemp')}</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-username">{t('operators.createDialog.username')}</Label>
                <Input
                  id="create-username"
                  autoComplete="off"
                  className="font-mono"
                  {...register('username')}
                />
                {errors.username && (
                  <p className="text-sm text-destructive" role="alert">
                    {t(String(errors.username.message))}
                  </p>
                )}
              </div>
              {mode === 'invite' ? (
                <div className="space-y-2">
                  <Label htmlFor="create-email">{t('operators.createDialog.email')}</Label>
                  <Input id="create-email" type="email" autoComplete="off" {...register('email')} />
                  {errors.email && (
                    <p className="text-sm text-destructive" role="alert">
                      {t(String(errors.email.message))}
                    </p>
                  )}
                  <p className="text-[13px] text-muted-foreground">
                    {t('operators.createDialog.inviteHint')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="create-password">{t('operators.createDialog.password')}</Label>
                  <div className="relative">
                    <Input
                      id="create-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      className="pr-10 font-mono"
                      {...register('password')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={
                        showPassword
                          ? t('operators.createDialog.hidePassword')
                          : t('operators.createDialog.showPassword')
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-destructive" role="alert">
                      {t(String(errors.password.message))}
                    </p>
                  )}
                  <p className="text-[13px] text-muted-foreground">
                    {t('operators.createDialog.tempHint')}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="create-role">{t('operators.createDialog.role')}</Label>
                <Select
                  value={role}
                  onValueChange={(v) =>
                    setValue('role', v as OperatorRole, { shouldValidate: true })
                  }
                >
                  <SelectTrigger id="create-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {t(`operators.roles.${r}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.role && (
                  <p className="text-sm text-destructive" role="alert">
                    {t(String(errors.role.message))}
                  </p>
                )}
              </div>
              {serverError && (
                <p className="text-sm text-destructive" role="alert" aria-live="polite">
                  {serverError}
                </p>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                >
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('operators.createDialog.creating')}
                    </>
                  ) : mode === 'invite' ? (
                    t('operators.createDialog.createInvite')
                  ) : (
                    t('operators.createDialog.create')
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
